
import { defineComponent, PropType } from 'vue';
import {
  formatWithLocale,
  DATE_TIME_FORMATS,
  parseISO,
  guessTimezone
} from '@white-label-helper/date-utilities';
import { convertTimeStringToObject } from '@white-label-helper/time-helpers';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { closeModal, popstateEventHandler, pushModalState } from '@white-label-helper/helper-history-modal';

// Types
import type { FormData } from '@white-label-types/component';
// Components
import SearchParkingEditForm from '../search-parking-edit-form/search-parking-edit-form.vue';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import {Partners} from "@white-label-types/partners";

export default defineComponent({
  components: {
    IconButton: () => import('@white-label-icon/icon-button'),
    IconPencil: () => import('@white-label-icon/icon-pencil'),
    SearchParkingEditForm
  },

  props: {
    formData: {
      type: Object as PropType<FormData>,
      required: true
    },

    updatingSearch: {
      type: Boolean,
      required: false
    },

    editMode: {
      type: Boolean,
      required: true
    },
    customErrorMessages: {
      type: Object,
      required: false,
      default: () => ({})
    },
    apiRequestRunning: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      removePopstateHandler: null,
      showDesktopBackdrop: false
    };
  },

  computed: {
    airports(): Partners['pois'] {
      return getAppVariable('pois');
    },
    searchWidgetTitle(): string {
      return this.airports?.find((item) => item.code === this.formData.airport)?.name || this.$t('searchEditWidget.title');
    },
    partnerTimeZone(): string {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    localEditMode: {
      get(): boolean {
        return this.editMode;
      },
      set(val: boolean) {
        this.$emit('update:editMode', val);
      }
    },

    localUpdatingSearch: {
      get(): boolean {
        return this.updatingSearch;
      },
      set(val: boolean) {
        this.$emit('update:updatingSearch', val);
      }
    },

    formattedEntryDate(): string {
      return this.formData.entryDate
        ? formatWithLocale(
          parseISO(this.formData.entryDate),
          DATE_TIME_FORMATS.month_day,
          this.$i18n.locale
        )
        : '';
    },

    formattedExitDate(): string {
      return this.formData.exitDate
        ? formatWithLocale(
          parseISO(this.formData.exitDate),
          DATE_TIME_FORMATS.month_day,
          this.$i18n.locale
        )
        : '';
    },

    formattedEntryTime(): string {
      return this.formattedTime('entryTime');
    },

    formattedExitTime(): string {
      return this.formattedTime('exitTime');
    },

    showEditForm(): boolean {
      return this.isDesktopResolution || this.localEditMode;
    },

    isScrollLocked(): boolean {
      return (
        (this.isMobileResolution || this.isTabletResolution) &&
        this.showEditForm
      );
    },

    isCustomerGroupsExists(): boolean {
      const customerGroups = getAppVariable(`products.lounges.customer_groups`)
        || getAppVariable(`products.fasttrack.customer_groups`);

      return Boolean(
        customerGroups
        && Object.keys(customerGroups).length > 0
        && this.$launchDarkly.variation('ECOM-2801_BUNDLES-PRE-PURCHASE') === true
      );
    },

    computedCustomers(): string {
      if (!this.formData.customers) {
        return '';
      }

      const { adult, senior, infant, child } = this.formData.customers;
      const label = [];

      const getAgeText = (key: 'senior' | 'adult' | 'child' | 'infant', count: number) => {
        return 'x ' + this.$tc(`shared.customers.${key}`, count);
      };

      if (senior) {
        label.push(`${senior} ${getAgeText('senior', senior)}`);
      }
      if (adult) {
        label.push(`${adult} ${getAgeText('adult', adult)}`);
      }
      if (child) {
        label.push(`${child} ${getAgeText('child', child)}`);
      }
      if (infant) {
        label.push(`${infant} ${getAgeText('infant', infant)}`);
      }

      return label.join(', ');
    }
  },
  watch: {
    localEditMode: {
      handler(value) {
        if (!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
          return;
        }
        if (value) {
          pushModalState('localEditMode');
        } else {
          closeModal();
        }
      }
    }
  },
  mounted() {
    this.$emit('mounted');

    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      this.removePopstateHandler = popstateEventHandler('localEditMode', this.onClickOutside);
      this.localEditMode = window.location.hash?.includes('localEditMode');
    }
  },
  created() {
    this.$emit('created');
  },
  beforeDestroy() {
    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      if (this.removePopstateHandler) {
        this.removePopstateHandler();
      }
    }
  },
  methods: {
    formattedTime(type: 'entryTime' | 'exitTime'): string {
      const timeData = this.formData[type];
      if (!timeData) return '';

      const countryCode = getAppVariable('poi.country.code');
      const isEnglishFormat = countryCode === 'US' || countryCode === 'GB';

      return isEnglishFormat ? timeData.name.replace(/\s/g, '') : timeData.value;
    },
    setSearchCriteria(data: { [key: string]: any }) {
      this.formData.entryDate = data.date || '';
      this.formData.entryTime = data.time
        ? convertTimeStringToObject(data.time)
        : null;
      this.formData.exitDate = data.exitDate || '';
      this.formData.exitTime = data.exitTime
        ? convertTimeStringToObject(data.exitTime)
        : null;
      this.formData.airport = data.airport || '';
      this.formData.terminal = data.terminal || '';
      this.formData.airline = data.airline || '';
    },
    onClickOutside(): void {
      this.onCancelChanges();

    },

    onCancelChanges(): void {
      if (this.showDesktopBackdrop) {
        this.showDesktopBackdrop = false;
      }
      this.localEditMode = false;
    },

    updateSearch(payload: FormData) {
      this.$emit('change-date-time', payload);
    },

    updateDesktopBackdrop(payload: boolean): void {
      this.showDesktopBackdrop = payload;
    }
  }
});
